import { Injectable } from '@angular/core'
import { Observable, Subject, map, tap, take } from 'rxjs'
import { isArray, omit } from 'lodash-es'
import { User, CreateUserInput, UpdateUserInput, UserPrefs } from '@app/models/user.model'
import { DELETE_USER, GET_USERS, CREATE_USER, UPDATE_USER, UPDATE_PREFS } from '@app/query/user.query'
import { TranslateService } from '@ngx-translate/core'
import { pick } from 'lodash-es'
import { ServicesSubscriberEvent } from '@app/models/others.model'
import { MessageService, ConfirmationService } from 'primeng/api'
import { serializeAs } from '@app/utils/serialize'
import { ModulesType } from '@app/models/client.model'
import { AuthService } from '@app/core/auth/services/auth.service'
import { ApiService } from '@app/core/graphql/services/api.service'
import { TranslateMessagesService } from '@app/shared/services/translate-messages.service'

@Injectable({
  providedIn: 'root',
})
export class UsersService {
  subscriber: Subject<ServicesSubscriberEvent> = new Subject<ServicesSubscriberEvent>()
  id: string = 'USER'
  sysMessage: any
  constructor(
    private API: ApiService,
    private translate: TranslateService,
    private translateMessagesService: TranslateMessagesService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private authService: AuthService
  ) {
    this.sysMessage = this.translateMessagesService.create(this.id)
  }

  getCurrentUser(): Observable<User> {
    return new Observable((observer) => {
      this.authService
        .getUser()
        .then((user) => {
          // getUser() can resolve null (no session) — legacy consumers have always received it
          observer.next(user as User)
        })
        .catch((e) => {
          observer.error(e)
        })
    })
  }
  getCurrentUserPrefs(): Observable<Partial<UserPrefs>> {
    return new Observable((observer) => {
      this.authService
        .getMe()
        .pipe(take(1))
        .subscribe((user: any) => {
          try {
            observer.next(JSON.parse(user.data.prefs))
          } catch (_e) {
            observer.next({})
          }
        })
    })
  }
  /**
   * @param silence Suppress the success toast. For prefs the user SETS deliberately (their KPIs,
   * their language) the confirmation is the point; for ones a click writes as a side effect — a
   * view toggle — announcing every write is noise, and it is what pushes such prefs into
   * browser-local storage instead of onto the user where they belong.
   */
  saveCurrentUserPrefs(payload: Partial<UserPrefs>, silence?: boolean) {
    return new Observable((observer) => {
      this.authService
        .getUser()
        .then((user) => {
          payload.id = Number(user!.id)
          this.savePrefs(payload, silence)
            .pipe(take(1))
            .subscribe((value) => {
              observer.next(value)
            })
        })
        .catch((e) => {
          observer.error(e)
        })
    })
  }
  savePrefs(payload: Partial<UserPrefs>, silence?: boolean) {
    return this.API.privateMutation<any>(UPDATE_PREFS, (data: any) => {
      return {
        data: serializeAs(UserPrefs, pick(Object.assign(payload, data), Object.keys(new UserPrefs()))),
      }
    }).pipe(
      map((data: any) => {
        if (data.success) {
          return data.data
        } else {
          return data
        }
      }),
      tap((data: any) => {
        if (silence) return
        if (data.success) {
          this.messageService.add({
            severity: 'success',
            icon: 'pi pi-check-circle color-success',
            ...this.sysMessage.message('PREFS_SAVE_SUCCESS'),
          })
        }
      })
    )
  }
  getUsers() {
    return this.API.privateQuery<any>(GET_USERS).pipe(
      map((response: any) => {
        if (response.success) {
          return Array.isArray(response.data) ? (response.data as User[]) : []
        } else {
          return [] as User[]
        }
      }),
      tap((users: User[] | []) => {
        this.subscriber.next({
          type: `GET_${this.id}s`,
          data: users,
        })
      })
    )
  }
  createUser(payload: Partial<User>, silence?: boolean) {
    // payload.country_code = environment.defaultCountryCode;
    return this.API.privateMutation<any>(CREATE_USER, (data: any) => {
      const mergedData = Object.assign({}, payload, data)
      const inputData = serializeAs(CreateUserInput, pick(mergedData, Object.keys(new CreateUserInput()))) as CreateUserInput

      return {
        data: inputData,
      }
    }).pipe(
      tap((data: any) => {
        if (silence) return data
        if (data.success) {
          this.messageService.add({
            severity: 'success',
            icon: 'pi pi-check-circle color-success',
            ...this.sysMessage.message('CREATE_SUCCESS', data.data as User),
          })
          this.subscriber.next({
            type: `SET_${this.id}`,
            data: data.data as User,
          })
        }
      })
    )
  }

  update(payload: Partial<User>, silence?: boolean) {
    return this.API.privateMutation<any>(UPDATE_USER, (data: any) => {
      let requestData: any = serializeAs(UpdateUserInput, pick(Object.assign(payload, data), Object.keys(new UpdateUserInput()))) as UpdateUserInput
      if (requestData.password != undefined && !requestData.password.trim()) {
        requestData = omit(requestData, 'password')
      }
      return {
        data: requestData,
      }
    }).pipe(
      tap((data: any) => {
        if (silence) return data
        if (data.success) {
          this.messageService.add({
            severity: 'success',
            icon: 'pi pi-check-circle color-success',
            ...this.sysMessage.message('UPDATE_SUCCESS', data.data as User),
          })
          this.subscriber.next({
            type: `UPDATE_${this.id}`,
            data: data.data as User,
          })
        }
      })
    )
  }
  safeDeleteUser(user: User, silence?: boolean) {
    return new Observable<any>((observer) => {
      this.confirmationService.confirm({
        ...this.sysMessage.confirm('CONFIRM_DELETE', user as User),
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: this.translate.instant('COMMON.BUTTONS.DELETE'),
        rejectLabel: this.translate.instant('COMMON.BUTTONS.CANCEL'),
        defaultFocus: 'reject',
        acceptButtonStyleClass: 'p-button-text',
        accept: () => {
          this.deleteUser(user.id, silence)
            .pipe(take(1))
            .subscribe((data: any) => {
              observer.next(data)
              observer.complete()
            })
        },
        reject: () => {
          observer.next({
            success: false,
          })
          observer.complete()
        },
      })
    })
  }
  deleteUser(userId: number | string, silence?: boolean) {
    return this.API.privateMutation<any>(DELETE_USER, (data: any) => {
      data.id = parseInt(userId as string)
      return data
    }).pipe(
      tap((data: any) => {
        if (silence) return data
        if (data.success) {
          this.messageService.add({
            severity: 'success',
            icon: 'pi pi-check-circle color-success',
            ...this.sysMessage.message('DELETE_SUCCESS', data.data as User),
          })
          this.subscriber.next({
            type: `DELETE_${this.id}`,
            data: data.data as Pick<User, 'id' | 'full_name'>,
          })
        }
      })
    )
  }

  /**
   *
   * @param gridStyle Grid style
   * @param module Current module
   */
  updateCurrentView(gridStyle: 'grid' | 'row', module: ModulesType) {
    this.getCurrentUserPrefs()
      .pipe(take(1))
      .subscribe((data) => {
        if (!data.currentView) data.currentView = '[]'
        let views: any = JSON.parse(data.currentView)

        if (isArray(views)) {
          if (views?.find((val: any) => val.module == module)?.view) {
            const currentModule = views?.find((val: any) => val.module == data?.currentModule)
            currentModule.view = gridStyle
          } else {
            views.push({ module: module, view: gridStyle })
          }
        } else {
          views = JSON.stringify([{ module: module, view: gridStyle }])
        }

        this.saveCurrentUserPrefs({
          currentView: views,
        }).subscribe(() => {/* noop */})
      })
  }

  /**
   * Get current view by module type
   * @param module
   */
  getCurrentView(module: ModulesType): Observable<'row' | 'grid'> {
    const subject = new Subject<'row' | 'grid'>()
    this.getCurrentUserPrefs()
      .pipe(take(1))
      .subscribe((data) => {
        if (!data.currentView) data.currentView = '[]'
        const view = JSON.parse(data.currentView)

        if (view.length && view?.find((val: any) => val.module == module)?.view) {
          subject.next(view?.find((val: any) => val.module == module)?.view)
        } else {
          this.updateCurrentView('row', module)
          subject.next('row')
        }
      })
    return subject.asObservable()
  }
}
